import React from 'react';
import { forwardRef } from '@heroui/system';
import { extendVariants, Checkbox, CheckboxProps } from '@heroui/react';
import cn from 'classnames';

const CustomSelectedCheckboxIcon: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={cn('relative hidden z-10', size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')}
  >
    <path
      d="M6.0001 10.78L3.2201 8.00005L2.27344 8.94005L6.0001 12.6667L14.0001 4.66671L13.0601 3.72672L6.0001 10.78Z"
      fill="white"
    />
  </svg>
);

const CustomIndeterminateCheckboxIcon: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={cn('relative hidden z-10', size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')}
  >
    <rect x="1" y="1" width="14" height="14" rx="5" fill="#0F1013" />
    <rect x="1" y="1" width="14" height="14" rx="5" stroke="#0F1013" strokeWidth="2" />
    <path d="M11.5 8.5H4.5V7.5H11.5V8.5Z" fill="white" />
  </svg>
);

const CustomCheckbox = extendVariants(Checkbox, {
  variants: {
    size: {
      sm: {
        wrapper: 'rounded-md',
        label: 'text-s-m',
      },
      md: {
        wrapper: 'rounded-[0.4375rem]',
        label: 'text-m-m',
      },
      lg: {
        wrapper: 'rounded-[0.5625rem]',
        label: 'text-l-m',
      },
    },
    color: {
      default: {
        base: ['inline-flex', 'm-0', 'items-center', 'cursor-pointer'],
        wrapper: [
          'before:border-base-40',
          'bg-transparent',
          'group-data-[hover=true]:before:bg-transparent',
          'group-data-[hover=true]:after:bg-transparent',
          'group-data-[hover=true]:before:border-base-10',
          'group-data-[selected=true]:before:bg-base-5',
          'group-data-[selected=true]:after:bg-base-5',
          'group-data-[selected=true]:group-data-[hover=true]:before:bg-base-5',
          'group-data-[selected=true]:group-data-[hover=true]:after:bg-base-5',
          'group-data-[selected=true]:[&_svg]:block',
        ],
      },
    },
    isInvalid: {
      true: {
        wrapper: [
          'before:border-danger',
          'group-data-[selected=true]:before:bg-danger',
          'group-data-[selected=true]:after:bg-danger',
        ],
      },
    },
  },
  defaultVariants: {
    color: 'default',
    size: 'lg',
  },
});

type CustomCheckboxProps = CheckboxProps & { hasWrapper?: boolean; wrapperClassName?: string };

const CheckboxWrapper = forwardRef<'input', Omit<CustomCheckboxProps, 'ref'>>((props, ref) => {
  const { size, hasWrapper, wrapperClassName, isIndeterminate, ...restProps } = props;

  const icon = isIndeterminate ? (
    <CustomIndeterminateCheckboxIcon size={size} />
  ) : (
    <CustomSelectedCheckboxIcon size={size} />
  );

  return hasWrapper ? (
    <div className={cn('pointer-events-none', wrapperClassName)}>
      <CustomCheckbox ref={ref} icon={icon} size={size} {...restProps} />
    </div>
  ) : (
    <CustomCheckbox ref={ref} icon={icon} size={size} {...restProps} />
  );
});

CheckboxWrapper.displayName = 'CheckboxWrapper';

export default CheckboxWrapper;
